import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useConfiguration } from '../../context/ConfigurationContext';
import { cities } from '../../data/impactsData';
import { impactsData } from '../../data/impactsData';

const AdminPanel = () => {
  const { isSuperAdmin, isAdmin, isCityAdmin, userRole, userCity, canManageCity, logout } = useAuth();
  const {
    cityDataAvailable,
    indicatorsWithData,
    cityApiKeys,
    isLoading,
    updateCityAvailability,
    updateCityApiKey,
    addIndicatorToCity,
    removeIndicatorFromCity,
    updateCityIndicators,
    resetConfiguration,
    exportConfiguration,
    importConfiguration,
    loadConfiguration
  } = useConfiguration();

  // Filter cities based on role
  const availableCities = isSuperAdmin() 
    ? cities 
    : isCityAdmin() && userCity 
      ? cities.filter(city => city.name === userCity)
      : [];

  // Get indicators grouped by impact only (no sectors) - Only the 4 main groups in exact order
  const getIndicatorsByImpact = useCallback(() => {
    // Exact 4 groups in the order shown: Infrastructure, Ecosystem, Economy, Social
    const groupOrder = [
      'Infrastructure and Urban Services',
      'Ecosystem and Environment',
      'Economy and Productivity',
      'Social Vulnerability'
    ];
    
    // Color mapping for each group
    const groupColors = {
      'Infrastructure and Urban Services': { border: '#c0392b', bg: '#f8d7da', text: '#721c24' }, // Red
      'Ecosystem and Environment': { border: '#f39c12', bg: '#fff3cd', text: '#856404' }, // Orange
      'Economy and Productivity': { border: '#f1c40f', bg: '#fff9e6', text: '#b8860b' }, // Yellow/Gold
      'Social Vulnerability': { border: '#27ae60', bg: '#d4edda', text: '#155724' } // Green
    };
    
    // Filter and order impacts to match the exact 4 groups, flattening all indicators
    const orderedImpacts = [];
    groupOrder.forEach(groupTitle => {
      const impact = impactsData.find(imp => imp.title === groupTitle);
      if (impact) {
        // Flatten all indicators from all sectors into a single array
        const allIndicators = [];
        impact.sectors.forEach(sector => {
          sector.indicators.forEach(indicator => {
            allIndicators.push({
              name: indicator.name,
              threshold: indicator.threshold,
              dataNeeded: indicator.dataNeeded
            });
          });
        });
        
        orderedImpacts.push({
          id: impact.id,
          title: impact.title,
          color: groupColors[groupTitle] || { border: '#3498db', bg: '#f0f7ff', text: '#2c3e50' },
          indicators: allIndicators
        });
      }
    });
    
    return orderedImpacts;
  }, []);

  const indicatorsByImpact = getIndicatorsByImpact();

  // Initialize all impacts as expanded by default
  const initialExpandedState = useMemo(() => {
    const state = {};
    indicatorsByImpact.forEach(impact => {
      state[impact.id] = true; // Default to expanded
    });
    return state;
  }, [indicatorsByImpact]);

  const [activeTab, setActiveTab] = useState('cities');
  const [selectedCity, setSelectedCity] = useState(userCity || '');

  // Update breadcrumbs when city is selected
  useEffect(() => {
    if (selectedCity) {
      localStorage.setItem('adminSelectedCity', selectedCity);
      window.dispatchEvent(new CustomEvent('adminCitySelected', { detail: { city: selectedCity } }));
    } else {
      localStorage.removeItem('adminSelectedCity');
      window.dispatchEvent(new CustomEvent('adminCitySelected', { detail: { city: null } }));
    }
  }, [selectedCity]);

  // Listen for breadcrumb navigation to reset state
  useEffect(() => {
    const handleAdminCitySelected = (e) => {
      // If city is cleared from breadcrumbs, reset to city management
      if (e.detail.city === null && selectedCity) {
        setSelectedCity('');
        setActiveTab('cities');
      }
    };
    
    window.addEventListener('adminCitySelected', handleAdminCitySelected);
    return () => window.removeEventListener('adminCitySelected', handleAdminCitySelected);
  }, [selectedCity]);
  const [editingApiKey, setEditingApiKey] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [exportData, setExportData] = useState('');
  const [expandedImpacts, setExpandedImpacts] = useState(() => initialExpandedState);
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingIndicatorChanges, setPendingIndicatorChanges] = useState({}); // { indicatorName: true/false }
  const [isCityEditMode, setIsCityEditMode] = useState(false);
  const [pendingCityChanges, setPendingCityChanges] = useState({}); // { cityName: { availability: true/false, apiKey: 'value' } }
  const [apiKeyVisible, setApiKeyVisible] = useState({}); // { cityName: true/false }

  // Toggle expand/collapse for an impact category
  const toggleImpact = (impactId) => {
    setExpandedImpacts(prev => {
      // Default to true if undefined (expanded by default)
      const currentState = prev[impactId] ?? true;
      return {
        ...prev,
        [impactId]: !currentState
      };
    });
  };

  useEffect(() => {
    if (!isAdmin()) {
      setMessage({ type: 'error', text: 'Access denied. Admin privileges required.' });
    }
  }, [isAdmin]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleCityToggle = async (city, currentValue) => {
    if (!canManageCity(city)) {
      showMessage('error', 'You do not have permission to manage this city.');
      return;
    }
    
    // If in edit mode, just track the change in pending state
    if (isCityEditMode) {
      // Get the current display value (pending change or actual value)
      const displayValue = pendingCityChanges[city]?.availability !== undefined
        ? pendingCityChanges[city].availability
        : currentValue;
      
      setPendingCityChanges(prev => ({
        ...prev,
        [city]: {
          ...prev[city],
          availability: !displayValue
        }
      }));
      return;
    }
    
    // Otherwise, apply immediately (backward compatibility)
    const result = await updateCityAvailability(city, !currentValue);
    if (result.success) {
      showMessage('success', `${city} availability ${!currentValue ? 'enabled' : 'disabled'}`);
      await loadConfiguration();
    } else {
      showMessage('error', `Failed to update ${city}: ${result.error}`);
    }
  };

  const handleApiKeyChange = (city, value) => {
    // If in edit mode, track in pending changes
    if (isCityEditMode) {
      setPendingCityChanges(prev => ({
        ...prev,
        [city]: {
          ...prev[city],
          apiKey: value
        }
      }));
    } else {
      // Otherwise, use the old behavior
      setEditingApiKey(prev => ({ ...prev, [city]: value }));
    }
  };

  const handleApiKeySave = async (city) => {
    if (!canManageCity(city)) {
      showMessage('error', 'You do not have permission to manage this city.');
      return;
    }
    
    // In edit mode, this shouldn't be called (handled by Save Changes button)
    if (!isCityEditMode) {
      const apiKey = editingApiKey[city] || cityApiKeys[city];
      const result = await updateCityApiKey(city, apiKey);
      if (result.success) {
        showMessage('success', `API key updated for ${city}`);
        setEditingApiKey(prev => {
          const newState = { ...prev };
          delete newState[city];
          return newState;
        });
        await loadConfiguration();
      } else {
        showMessage('error', `Failed to update API key: ${result.error}`);
      }
    }
  };

  const handleCityEditClick = () => {
    setIsCityEditMode(true);
    setPendingCityChanges({});
  };

  const handleCityCancelEdit = () => {
    setIsCityEditMode(false);
    setPendingCityChanges({});
    setEditingApiKey({});
    showMessage('info', 'Changes cancelled');
  };

  const handleCitySaveChanges = async () => {
    const changes = Object.entries(pendingCityChanges);
    let hasErrors = false;
    let successCount = 0;
    
    for (const [cityName, changes] of changes) {
      if (!canManageCity(cityName)) {
        continue;
      }
      
      // Update availability if changed
      if (changes.availability !== undefined) {
        const currentAvailability = cityDataAvailable[cityName] || false;
        if (currentAvailability !== changes.availability) {
          const result = await updateCityAvailability(cityName, changes.availability);
          if (result.success) {
            successCount++;
          } else {
            hasErrors = true;
            showMessage('error', `Failed to update ${cityName} availability: ${result.error}`);
          }
        }
      }
      
      // Update API key if changed
      if (changes.apiKey !== undefined) {
        const currentApiKey = cityApiKeys[cityName] || '';
        if (currentApiKey !== changes.apiKey) {
          const result = await updateCityApiKey(cityName, changes.apiKey);
          if (result.success) {
            successCount++;
          } else {
            hasErrors = true;
            showMessage('error', `Failed to update ${cityName} API key: ${result.error}`);
          }
        }
      }
    }
    
    if (!hasErrors && changes.length > 0) {
      showMessage('success', `Successfully saved ${successCount} city change(s)`);
      await loadConfiguration();
    } else if (changes.length === 0) {
      showMessage('info', 'No changes to save');
    }
    
    setIsCityEditMode(false);
    setPendingCityChanges({});
    setEditingApiKey({});
  };

  const handleIndicatorToggle = async (city, indicatorName, isEnabled) => {
    if (!canManageCity(city)) {
      showMessage('error', 'You do not have permission to manage indicators for this city.');
      return;
    }
    
    // If in edit mode, just track the change in pending state
    if (isEditMode) {
      setPendingIndicatorChanges(prev => ({
        ...prev,
        [indicatorName]: !isEnabled
      }));
      return;
    }
    
    // Otherwise, apply immediately (backward compatibility)
    let result;
    if (isEnabled) {
      result = await removeIndicatorFromCity(city, indicatorName);
    } else {
      result = await addIndicatorToCity(city, indicatorName);
    }

    if (result.success) {
      showMessage('success', `${indicatorName} ${isEnabled ? 'removed from' : 'added to'} ${city}`);
      await loadConfiguration();
    } else {
      showMessage('error', `Failed to update indicator: ${result.error}`);
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setPendingIndicatorChanges({});
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setPendingIndicatorChanges({});
    showMessage('info', 'Changes cancelled');
  };

  const handleSaveChanges = async () => {
    if (!selectedCity) return;
    
    // Get current enabled indicators
    const currentEnabled = indicatorsWithData[selectedCity] || [];
    
    // Apply all pending changes
    const changes = Object.entries(pendingIndicatorChanges);
    let hasErrors = false;
    let successCount = 0;
    
    for (const [indicatorName, shouldBeEnabled] of changes) {
      const isCurrentlyEnabled = currentEnabled.includes(indicatorName);
      
      // Only make API call if state is actually changing
      if (isCurrentlyEnabled !== shouldBeEnabled) {
        let result;
        if (shouldBeEnabled) {
          result = await addIndicatorToCity(selectedCity, indicatorName);
        } else {
          result = await removeIndicatorFromCity(selectedCity, indicatorName);
        }
        
        if (result.success) {
          successCount++;
        } else {
          hasErrors = true;
          showMessage('error', `Failed to update ${indicatorName}: ${result.error}`);
        }
      }
    }
    
    if (!hasErrors && changes.length > 0) {
      showMessage('success', `Successfully saved ${successCount} indicator change(s)`);
      await loadConfiguration();
    } else if (changes.length === 0) {
      showMessage('info', 'No changes to save');
    }
    
    setIsEditMode(false);
    setPendingIndicatorChanges({});
  };

  const handleBulkIndicatorUpdate = async (city, selectedIndicators) => {
    const result = await updateCityIndicators(city, selectedIndicators);
    if (result.success) {
      showMessage('success', `Indicators updated for ${city}`);
      await loadConfiguration();
    } else {
      showMessage('error', `Failed to update indicators: ${result.error}`);
    }
  };

  const handleReset = async () => {
    if (!isSuperAdmin()) {
      showMessage('error', 'Only super admin can reset configurations.');
      return;
    }
    if (window.confirm('Are you sure you want to reset all configurations to defaults? This cannot be undone.')) {
      const result = await resetConfiguration();
      if (result.success) {
        showMessage('success', 'Configuration reset to defaults');
        await loadConfiguration();
      } else {
        showMessage('error', `Failed to reset: ${result.error}`);
      }
    }
  };

  const handleExport = async () => {
    const result = await exportConfiguration();
    if (result.success) {
      const jsonStr = JSON.stringify(result.data, null, 2);
      setExportData(jsonStr);
      showMessage('success', 'Configuration exported. Copy the JSON below.');
    } else {
      showMessage('error', `Failed to export: ${result.error}`);
    }
  };

  const handleImport = async () => {
    try {
      const configData = JSON.parse(exportData);
      const result = await importConfiguration(configData);
      if (result.success) {
        showMessage('success', 'Configuration imported successfully');
        setExportData('');
        await loadConfiguration();
      } else {
        showMessage('error', `Failed to import: ${result.error}`);
      }
    } catch (err) {
      showMessage('error', `Invalid JSON format: ${err.message}`);
    }
  };

  if (!isAdmin()) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You need admin privileges to access this panel.</p>
        <button onClick={logout} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
          Logout
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.25rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.25rem' }}>
        <h1 style={{ fontSize: '1.75rem', margin: 0 }}>Admin Configuration Panel</h1>
        {isCityAdmin() && userCity && (
          <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
            City Admin for <strong>{userCity}</strong>
          </p>
        )}
      </div>

      {message.text && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '4px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Tabs - Only Super Admin has access */}
      {isSuperAdmin() ? (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', borderBottom: '2px solid #ddd' }}>
          <button
            onClick={() => {
              setActiveTab('cities');
              setSelectedCity(''); // Clear selected city when going back to city management
              setIsCityEditMode(false); // Reset edit mode
              setPendingCityChanges({}); // Clear pending changes
            }}
            style={{
              padding: '0.6rem 1.25rem',
              border: 'none',
              background: activeTab === 'cities' ? '#3498db' : 'transparent',
              color: activeTab === 'cities' ? 'white' : '#333',
              cursor: 'pointer',
              borderBottom: activeTab === 'cities' ? '2px solid #3498db' : 'none',
              fontSize: '0.9rem'
            }}
          >
            City Management
          </button>
          {selectedCity && (
            <button
              onClick={() => setActiveTab('indicators')}
              style={{
                padding: '0.6rem 1.25rem',
                border: 'none',
                background: activeTab === 'indicators' ? '#3498db' : 'transparent',
                color: activeTab === 'indicators' ? 'white' : '#333',
                cursor: 'pointer',
                borderBottom: activeTab === 'indicators' ? '2px solid #3498db' : 'none',
                fontWeight: activeTab === 'indicators' ? 600 : 400,
                fontSize: '0.9rem'
              }}
            >
              {selectedCity}
            </button>
          )}
          <button
            onClick={() => setActiveTab('import-export')}
            style={{
              padding: '0.6rem 1.25rem',
              border: 'none',
              background: activeTab === 'import-export' ? '#3498db' : 'transparent',
              color: activeTab === 'import-export' ? 'white' : '#333',
              cursor: 'pointer',
              borderBottom: activeTab === 'import-export' ? '2px solid #3498db' : 'none',
              fontSize: '0.9rem'
            }}
          >
            Import/Export
          </button>
        </div>
      ) : (
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffc107', 
          borderRadius: '4px',
          marginBottom: '2rem',
          color: '#856404'
        }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Access Restricted</h3>
          <p>City admins do not have permission to manage configurations. Only super admin can manage cities and indicators.</p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
            You are logged in as <strong>{userRole}</strong> for <strong>{userCity}</strong>.
          </p>
        </div>
      )}

      {/* City Management Tab - Only for Super Admin */}
      {isSuperAdmin() && activeTab === 'cities' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>City Configuration</h2>
            {!isCityEditMode ? (
              <button
                onClick={handleCityEditClick}
                style={{
                  padding: '0.4rem 1rem',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  transition: 'background-color 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2980b9'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3498db'}
              >
                Edit
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button
                  onClick={handleCityCancelEdit}
                  style={{
                    padding: '0.4rem 1rem',
                    backgroundColor: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    transition: 'background-color 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7f8c8d'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#95a5a6'}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCitySaveChanges}
                  style={{
                    padding: '0.4rem 1rem',
                    backgroundColor: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    transition: 'background-color 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#229954'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#27ae60'}
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>
          
          {isCityEditMode && (
            <div style={{
              padding: '0.4rem 0.75rem',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              marginBottom: '0.75rem',
              fontSize: '0.8rem',
              color: '#856404'
            }}>
              <strong>Edit Mode:</strong> Make your changes and click "Save Changes" to apply them.
            </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '1rem',
            marginTop: '0.75rem'
          }}
          className="city-management-grid"
          >
            {availableCities.map(city => (
              <div
                key={city.name}
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #e0e0e0',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  // Don't trigger if clicking on interactive elements or in edit mode
                  if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON' || e.target.closest('label') || isCityEditMode) {
                    return;
                  }
                  // Open Indicator Management for this city
                  setSelectedCity(city.name);
                  setActiveTab('indicators');
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                {/* City Image */}
                <div
                  style={{
                    width: '100%',
                    height: '120px',
                    backgroundImage: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.5)), url('${process.env.PUBLIC_URL}/${city.image}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}
                >
                  <h3 style={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 700,
                    textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                    margin: 0,
                    textAlign: 'center'
                  }}>
                    {city.name}
                  </h3>
                  {!isCityEditMode && (
                    <div style={{
                      position: 'absolute',
                      top: '0.4rem',
                      right: '0.4rem',
                      backgroundColor: 'rgba(52, 152, 219, 0.9)',
                      color: 'white',
                      padding: '0.2rem 0.4rem',
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      pointerEvents: 'none'
                    }}>
                      Click to Manage Indicators
                    </div>
                  )}
                </div>

                {/* City Configuration */}
                <div style={{ padding: '0.75rem' }}>
                  {/* Data Available Toggle */}
                  <div style={{ marginBottom: isCityEditMode ? '0.75rem' : '0' }}>
                    <label 
                      onClick={(e) => e.stopPropagation()} // Prevent card click
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        cursor: (isCityEditMode && canManageCity(city.name)) ? 'pointer' : 'not-allowed',
                        fontSize: '0.85rem'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          isCityEditMode && pendingCityChanges[city.name]?.availability !== undefined
                            ? pendingCityChanges[city.name].availability
                            : (cityDataAvailable[city.name] || false)
                        }
                        onChange={(e) => {
                          e.stopPropagation(); // Prevent card click
                          // Get current display value for toggle
                          const currentDisplayValue = isCityEditMode && pendingCityChanges[city.name]?.availability !== undefined
                            ? pendingCityChanges[city.name].availability
                            : (cityDataAvailable[city.name] || false);
                          handleCityToggle(city.name, currentDisplayValue);
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent card click
                        disabled={!isCityEditMode || !canManageCity(city.name)}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: (isCityEditMode && canManageCity(city.name)) ? 'pointer' : 'not-allowed',
                          opacity: (!isCityEditMode || !canManageCity(city.name)) ? 0.6 : 1,
                          accentColor: (isCityEditMode && pendingCityChanges[city.name]?.availability !== undefined
                            ? pendingCityChanges[city.name].availability
                            : (cityDataAvailable[city.name] || false)) ? '#27ae60' : undefined
                        }}
                      />
                      <span style={{
                        fontWeight: 700,
                        color: (isCityEditMode && pendingCityChanges[city.name]?.availability !== undefined
                          ? pendingCityChanges[city.name].availability
                          : (cityDataAvailable[city.name] || false)) ? '#27ae60' : '#e74c3c'
                      }}>
                        {(isCityEditMode && pendingCityChanges[city.name]?.availability !== undefined
                          ? pendingCityChanges[city.name].availability
                          : (cityDataAvailable[city.name] || false)) ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </label>
                  </div>

                  {/* API Key Input - Always visible with eye toggle */}
                  <div style={{ marginBottom: '0' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#666',
                      marginBottom: '0.2rem'
                    }}>
                      API Key:
                    </label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <input
                        type={apiKeyVisible[city.name] ? 'text' : 'password'}
                        value={
                          isCityEditMode && pendingCityChanges[city.name]?.apiKey !== undefined
                            ? pendingCityChanges[city.name].apiKey
                            : (cityApiKeys[city.name] || '')
                        }
                        onChange={(e) => {
                          if (!isCityEditMode) return;
                          e.stopPropagation(); // Prevent card click
                          handleApiKeyChange(city.name, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent card click
                        onFocus={(e) => {
                          e.stopPropagation(); // Prevent card click
                          if (!isCityEditMode) {
                            e.target.blur();
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.4rem 2.5rem 0.4rem 0.4rem',
                          border: isCityEditMode && pendingCityChanges[city.name]?.apiKey !== undefined
                            ? '1px solid #ffc107'
                            : '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          backgroundColor: (!isCityEditMode || !canManageCity(city.name)) ? '#f5f5f5' : '#fff',
                          cursor: (isCityEditMode && canManageCity(city.name)) ? 'text' : 'not-allowed',
                          color: !isCityEditMode ? '#666' : '#000'
                        }}
                        placeholder="API Key"
                        disabled={!isCityEditMode || !canManageCity(city.name)}
                        readOnly={!isCityEditMode}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setApiKeyVisible(prev => ({
                            ...prev,
                            [city.name]: !prev[city.name]
                          }));
                        }}
                        style={{
                          position: 'absolute',
                          right: '0.5rem',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#666',
                          opacity: 1
                        }}
                        title={apiKeyVisible[city.name] ? 'Hide API Key' : 'Show API Key'}
                      >
                        {apiKeyVisible[city.name] ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Indicator Management Tab - Only for Super Admin */}
      {isSuperAdmin() && activeTab === 'indicators' && selectedCity && (
        <div className="indicator-management-container">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: '#2c3e50' }}>Indicators for {selectedCity}</h2>
                {!isEditMode ? (
                  <button
                    onClick={handleEditClick}
                    style={{
                      padding: '0.5rem 1.25rem',
                      backgroundColor: '#3498db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      transition: 'background-color 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2980b9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3498db'}
                  >
                    Edit
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={handleCancelEdit}
                      style={{
                        padding: '0.5rem 1.25rem',
                        backgroundColor: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        transition: 'background-color 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7f8c8d'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#95a5a6'}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveChanges}
                      style={{
                        padding: '0.5rem 1.25rem',
                        backgroundColor: '#27ae60',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        transition: 'background-color 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#229954'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#27ae60'}
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </div>
              
              {isEditMode && (
                <div style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.85rem',
                  color: '#856404'
                }}>
                  <strong>Edit Mode:</strong> Make your changes and click "Save Changes" to apply them.
                </div>
              )}
              
              {/* Grouped by Impact - Premium Compact Grid Layout */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '0.75rem',
                width: '100%',
                overflow: 'visible'
              }}>
                {indicatorsByImpact.map((impact) => (
                  <div
                    key={impact.id}
                    style={{
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                      overflow: 'visible',
                      border: '1px solid #e8e8e8',
                      display: 'flex',
                      flexDirection: 'column',
                      height: 'auto'
                    }}
                  >
                    {/* Impact Card Header */}
                    <div
                      onClick={() => toggleImpact(impact.id)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: impact.color.bg,
                        borderLeft: `3px solid ${impact.color.border}`,
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        color: impact.color.text,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.15s ease',
                        borderRadius: (expandedImpacts[impact.id] !== false) ? '8px 8px 0 0' : '8px',
                        flexShrink: 0
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: '1.2' }}>{impact.title}</span>
                      <span 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleImpact(impact.id);
                        }}
                        style={{ 
                          fontSize: '0.7rem', 
                          color: impact.color.text,
                          fontWeight: 400,
                          cursor: 'pointer',
                          padding: '0.15rem 0.3rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '18px',
                          userSelect: 'none',
                          pointerEvents: 'auto',
                          opacity: 0.7
                        }}
                      >
                        {(expandedImpacts[impact.id] !== false) ? '▼' : '▶'}
                      </span>
                    </div>

                    {/* Indicators List - Compact Premium */}
                    {expandedImpacts[impact.id] !== false && (
                    <div style={{ 
                      padding: '0.4rem 0.6rem',
                      backgroundColor: '#fff',
                      borderRadius: '0 0 8px 8px'
                    }}>
                      {impact.indicators.map((indicator, index) => {
                        const currentEnabled = indicatorsWithData[selectedCity]?.includes(indicator.name) || false;
                        // In edit mode, use pending changes; otherwise use current state
                        const displayEnabled = isEditMode 
                          ? (pendingIndicatorChanges[indicator.name] !== undefined 
                              ? pendingIndicatorChanges[indicator.name] 
                              : currentEnabled)
                          : currentEnabled;
                        const isLast = index === impact.indicators.length - 1;
                        const hasPendingChange = isEditMode && pendingIndicatorChanges[indicator.name] !== undefined;
                        return (
                          <div
                            key={indicator.name}
                            style={{
                              padding: '0.35rem 0.5rem',
                              borderBottom: isLast ? 'none' : '1px solid #f5f5f5',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '0.4rem',
                              transition: 'all 0.15s ease',
                              backgroundColor: displayEnabled ? '#f0f9f4' : 'transparent',
                              borderRadius: isLast ? '0 0 6px 6px' : '0',
                              borderLeft: hasPendingChange ? '2px solid #ffc107' : 'none',
                              paddingLeft: hasPendingChange ? '0.3rem' : '0.5rem'
                            }}
                            onMouseEnter={(e) => {
                              if (isEditMode) {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (isEditMode) {
                                e.currentTarget.style.backgroundColor = displayEnabled ? '#f0f9f4' : 'transparent';
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={displayEnabled}
                              onChange={() => handleIndicatorToggle(selectedCity, indicator.name, displayEnabled)}
                              disabled={!isEditMode}
                              style={{
                                marginTop: '0.1rem',
                                cursor: isEditMode ? 'pointer' : 'not-allowed',
                                width: '14px',
                                height: '14px',
                                flexShrink: 0,
                                opacity: isEditMode ? 1 : 0.6
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 400,
                                  fontSize: '0.75rem',
                                  color: '#2c3e50',
                                  cursor: 'pointer',
                                  lineHeight: '1.4'
                                }}
                                onClick={() => {
                                  if (isEditMode) {
                                    handleIndicatorToggle(selectedCity, indicator.name, displayEnabled);
                                  }
                                }}
                              >
                                {indicator.name}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                ))}
              </div>
        </div>
      )}

      {/* Import/Export Tab - Only for Super Admin */}
      {activeTab === 'import-export' && isSuperAdmin() && (
        <div>
          <h2>Import/Export Configuration</h2>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button
              onClick={handleExport}
              style={{ padding: '0.75rem 1.5rem', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Export Configuration
            </button>
            <button
              onClick={handleReset}
              style={{ padding: '0.75rem 1.5rem', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Reset to Defaults
            </button>
          </div>

          {exportData && (
            <div style={{ marginTop: '2rem' }}>
              <h3>Exported Configuration</h3>
              <textarea
                value={exportData}
                onChange={(e) => setExportData(e.target.value)}
                style={{ width: '100%', minHeight: '300px', padding: '1rem', fontFamily: 'monospace', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                placeholder="Paste configuration JSON here to import..."
              />
              <div style={{ marginTop: '1rem' }}>
                <button
                  onClick={handleImport}
                  style={{ padding: '0.75rem 1.5rem', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Import Configuration
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
